import React, { useState } from 'react'

import { ServiceProvider, UserCompanyDto, UserProfileDto } from '../../services/api.types'
import { Box, List, ListItem, ListItemIcon } from '@material-ui/core'
import { t } from 'ttag'

import YumButton from '../../components/YumButton/YumButton'

import Typography from '@material-ui/core/Typography'
import makeStyles from '@material-ui/core/styles/makeStyles'
import ListItemText from '@material-ui/core/ListItemText'

import { ReactComponent as CheckedCircleIcon } from '../../assets/v2/icons/checked-circle-blue.svg'
import { ReactComponent as EmptyCircleIcon } from '../../assets/v2/icons/empty-circle-blue.svg'
import { ReactComponent as GoogleIcon } from '../../assets/v2/icons/icon-google.svg'
import { ReactComponent as DropdownIcon } from '../../assets/v3/icons/notifications/dropdown.svg'

import NewAppleHealthIcon from '../../assets/v3/icons/integration/apple-health.svg'
import NewHealthConnectIcon from '../../assets/v3/icons/integration/health-connect.svg'
import FitbitIcon from '../RegisterUser/images/fitbit.svg'
import GarminIcon from '../RegisterUser/images/garmin.svg'
import GoogleFitIcon from '../RegisterUser/images/google-fit.svg'
import PolarIcon from '../RegisterUser/images/polar.svg'
import SuuntoIcon from '../RegisterUser/images/suunto.svg'

import { isAndroid, isIOS } from '../../common/helpers/isNative'
import { ROUTES } from '../../common/Routes/routePaths'
import YumCancelButton from '../../components/YumCancelButton/YumCancelButton'
import { darkBlue, primaryBlue } from '../../styles/material-ui-theme-v2'
import { Card } from '../../components/Card/Card'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import { DesktopSidebar } from '../../components/DesktopSidebar/DesktopSidebar'
import { hasFeatureFlag } from '../../common/helpers/featureFlags'

export interface IIntegrationsProps {
  userDto: UserProfileDto
  lastUpdate: number | null
  userCompanies: UserCompanyDto[]
  isDuringRegistration?: boolean
}

export interface IIntegrationsDispatchProps {
  registerAppleHealth(): void
  registerWithoutIntegration(): void
  registerHealthConnect(): void
}

const useStyles = makeStyles((theme) => ({
  title: { marginTop: '30px', marginBottom: '20px' },
  fullWidthContainer: {
    width: '100%',
    flexBasis: '100%',
    flexGrow: 0,
    flexShrink: 0,
  },
  iconsContainer: {
    padding: '10px 0 0',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    alignItems: 'center',
    fontSize: '13px',
  },
  googleFitButton: {
    width: 'auto',
    whiteSpace: 'nowrap',
    margin: '0 auto',
    lineHeight: '18px',
    display: 'inline-flex',
    textTransform: 'unset',

    fontFamily: 'Roboto, arial, sans-serif',
    fontSize: '16px',
    fontWeight: 500,
    letterSpacing: '.21px',

    backgroundColor: '#4285f4',
    padding: 0,
    border: '1px solid #4285f4',
    overflow: 'hidden',

    borderRadius: '2px',
    transition: 'none',
  },
  listItem: { marginBottom: '16px', flexWrap: 'wrap' },
  listItemDisabled: {
    opacity: 0.5,
    borderColor: 'transparent !important',
    cursor: 'default',
    backgroundColor: '#F5F5F9 !important',
  },
  actionButtons: { display: 'flex', /*marginTop: '40px',*/ gap: '24px', justifyContent: 'center' },
  link: {
    color: primaryBlue,
    cursor: 'pointer',
    '@media (hover:hover)': {
      '&:hover': { textDecoration: 'underline' },
    },
    '&:active': { textDecoration: 'underline' },
  },
  dropdown: { display: 'flex', gap: '4px', alignItems: 'center' },
  dropdownIcon: {
    color: primaryBlue,
    transition: 'transform 150ms',
  },
  divider: { height: '1px', background: darkBlue, width: '100%', opacity: 0.5 },
  worksWithGrid: { display: 'grid', gridTemplateColumns: 'auto auto', gap: '10px', width: '100%' },
  primaryTypography: {
    fontWeight: 'bold',
    fontSize: '15px',
  },
  secondaryTypography: {
    fontSize: '13px',
    color: darkBlue,
  },
}))

export default function Integrations(props: IIntegrationsProps & IIntegrationsDispatchProps) {
  const classes = useStyles()
  const {
    userDto,
    lastUpdate,
    isDuringRegistration,
    userCompanies,
    registerAppleHealth,
    registerWithoutIntegration,
    registerHealthConnect,
  } = props

  React.useEffect(() => {
    if (!isDuringRegistration) {
      setTimeout(() => {
        document.body.classList.add('show-back-btn')
      }, 0)
    }
    return function cleanup() {
      if (!isDuringRegistration) {
        document.body.classList.remove('show-back-btn')
      }
    }
  }, [])

  const [showOldGoogleFit, setShowOldGoogleFit] = useState(false)
  const [hideHealthConnect, setHideHealthConnect] = useState(true)

  React.useEffect(() => {
    const { serviceProvider: currentServiceProvider } = userDto
    setShowOldGoogleFit(
      currentServiceProvider === ServiceProvider.GOOGLE_FIT || hasFeatureFlag(userCompanies, 'showOldGoogleFit'),
    )
    setHideHealthConnect(
      currentServiceProvider !== ServiceProvider.HEALTH_CONNECT && hasFeatureFlag(userCompanies, 'hideHealthConnect'),
    )
  }, [userCompanies])

  const [submitInProgress, setSubmitInProgress] = useState(false)
  const [integration, setIntegration] = useState(undefined as string | null | undefined)

  const [isAppleHealthOpen, setIsAppleHealthOpen] = useState(false)
  const [isHealthConnectOpen, setIsHealthConnectOpen] = useState(false)

  React.useEffect(() => {
    setSubmitInProgress(false)
  }, [lastUpdate])

  const onClickIntegration = () => {
    const serviceProvider = integration
    if (serviceProvider === ServiceProvider.APPLE_HEALTH) {
      setSubmitInProgress(true)
      registerAppleHealth()
    } else if (serviceProvider === ServiceProvider.HEALTH_CONNECT) {
      setSubmitInProgress(true)
      registerHealthConnect()
    } else if (serviceProvider === null) {
      setSubmitInProgress(true)
      registerWithoutIntegration()
    }
  }

  const doIntegration = () => {
    if (
      integration === ServiceProvider.APPLE_HEALTH ||
      integration === ServiceProvider.HEALTH_CONNECT ||
      integration === null
    ) {
      return ''
    }

    const { dateOfBirth } = userDto
    const scope = !dateOfBirth ? 'LEAN' : 'FULL'
    return integration ? `${ROUTES.INTEGRATION_PROXY.to}?type=${integration}&scope=${scope}` : ''
  }

  const handleAppleHealthToggle = () => {
    setIsAppleHealthOpen(!isAppleHealthOpen)
  }

  const handleHealthConnectToggle = () => {
    setIsHealthConnectOpen(!isHealthConnectOpen)
  }

  const appleHealthIcons = (
    <div className={classes.iconsContainer}>
      <div className={classes.divider}></div>
      <div>
        <div className={classes.dropdown} onClick={handleAppleHealthToggle}>
          <DropdownIcon
            className={classes.dropdownIcon}
            style={{
              transform: isAppleHealthOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
          <Box className={classes.link}>{t`Works with`}</Box>
        </div>
      </div>
      {isAppleHealthOpen && (
        <div className={classes.worksWithGrid}>
          <div>
            Apple iPhone
            <br />
            Apple Watch
            <br />
            adidas Running
            <br />
            Coros
            <br />
            HUAWEI Health
            <br />
            Mi Fitness
          </div>
          <div>
            Oura
            <br />
            Strava
            <br />
            Zepp Life (MiFit)
            <br />
            Zepp (Amazfit)
            <br />
            Withings
            <br />
            Whoop
          </div>
        </div>
      )}
    </div>
  )

  const healthConnectIcons = (
    <div className={classes.iconsContainer}>
      <div className={classes.divider}></div>
      <div>
        <div className={classes.dropdown} onClick={handleHealthConnectToggle}>
          <DropdownIcon
            className={classes.dropdownIcon}
            style={{
              transform: isHealthConnectOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
          <Box className={classes.link}>{t`Works with`}</Box>
        </div>
      </div>
      {isHealthConnectOpen && (
        <div className={classes.worksWithGrid}>
          <div>
            Android
            <br />
            adidas Running
            <br />
            Coros
            <br />
            Fitbit
            <br />
            Google Fit
            <br />
            Oura
          </div>
          <div>
            Samsung Health
            <br />
            Wahoo
            <br />
            WHOOP
            <br />
            Withings
            <br />
            Zepp (Amazfit)
            <br />
            ...
          </div>
        </div>
      )}
    </div>
  )

  const Title = ({ children }: { children: any }) => (
    <Typography variant="h2" className={classes.title}>
      {children}
    </Typography>
  )

  const phoneIntegrations = (
    <>
      {/*<Title>{t`Phone integration`}</Title>*/}
      <List>
        <ListItem
          button
          disableRipple
          selected={integration === 'APPLE_HEALTH'}
          onClick={isIOS ? () => setIntegration('APPLE_HEALTH') : () => {}}
          className={isIOS ? classes.listItem : [classes.listItem, classes.listItemDisabled].join(' ')}
        >
          <ListItemIcon>
            <img style={{ width: '36px' }} src={NewAppleHealthIcon} alt={`Apple Health`} />
          </ListItemIcon>
          <ListItemText
            primary={'Apple Health'}
            secondary={t`iOS device`}
            primaryTypographyProps={{
              className: classes.primaryTypography,
            }}
            secondaryTypographyProps={{
              className: classes.secondaryTypography,
            }}
          />
          <ListItemIcon>{integration === 'APPLE_HEALTH' ? <CheckedCircleIcon /> : <EmptyCircleIcon />}</ListItemIcon>
          <div className={classes.fullWidthContainer}>
            <div className="integration-disabled-text">
              {!isIOS ? t`To use Apple Health, use YuMuuv iOS app` : t`Make sure your data is synced to Apple Health`}
            </div>
            {appleHealthIcons}
          </div>
        </ListItem>
        {!hideHealthConnect && (
          <ListItem
            button
            disableRipple
            selected={integration === 'HEALTH_CONNECT'}
            onClick={isAndroid ? () => setIntegration('HEALTH_CONNECT') : () => {}}
            className={isAndroid ? classes.listItem : [classes.listItem, classes.listItemDisabled].join(' ')}
          >
            <ListItemIcon>
              <img style={{ width: '36px' }} src={NewHealthConnectIcon} alt={`Health Connect`} />
            </ListItemIcon>
            <ListItemText
              primary={'Health Connect'}
              secondary={t`Android device`}
              primaryTypographyProps={{
                className: classes.primaryTypography,
              }}
              secondaryTypographyProps={{
                className: classes.secondaryTypography,
              }}
            />
            <ListItemIcon>
              {integration === 'HEALTH_CONNECT' ? <CheckedCircleIcon /> : <EmptyCircleIcon />}
            </ListItemIcon>
            <div className={classes.fullWidthContainer}>
              <div className="integration-disabled-text">
                {!isAndroid
                  ? t`To use Health Connect, use YuMuuv Android app`
                  : t`Make sure your data is synced to Health Connect`}
              </div>
              {healthConnectIcons}
            </div>
          </ListItem>
        )}
      </List>
    </>
  )

  const mainForm = (
    <>
      {phoneIntegrations}

      {/*<Title>{t`Cloud integration`}</Title>*/}
      <List>
        <ListItem
          button
          disableRipple
          onClick={() => setIntegration('FITBIT')}
          selected={integration === 'FITBIT'}
          className={classes.listItem}
        >
          <ListItemIcon>
            <img className="fitbit" src={FitbitIcon} alt={`Fitbit`} />
          </ListItemIcon>
          <ListItemText primary={'' /*`Fitbit`*/} />
          <ListItemIcon>{integration === 'FITBIT' ? <CheckedCircleIcon /> : <EmptyCircleIcon />}</ListItemIcon>
        </ListItem>

        <ListItem
          button
          disableRipple
          onClick={() => setIntegration('GARMIN')}
          selected={integration === 'GARMIN'}
          className={classes.listItem}
        >
          <ListItemIcon>
            <img className="garmin" src={GarminIcon} alt={`Garmin`} />
          </ListItemIcon>
          <ListItemText primary={'' /*`Garmin Connect`*/} />
          <ListItemIcon>{integration === 'GARMIN' ? <CheckedCircleIcon /> : <EmptyCircleIcon />}</ListItemIcon>
        </ListItem>

        {showOldGoogleFit && (
          <ListItem
            button
            disableRipple
            onClick={() => setIntegration('GOOGLE_FIT')}
            selected={integration === 'GOOGLE_FIT'}
            className={classes.listItem}
          >
            <ListItemIcon>
              <img className="google-fit" src={GoogleFitIcon} alt={`Google Fit`} />
            </ListItemIcon>
            <ListItemText primary={'' /*`Google Fit`*/} />
            <ListItemIcon>{integration === 'GOOGLE_FIT' ? <CheckedCircleIcon /> : <EmptyCircleIcon />}</ListItemIcon>
          </ListItem>
        )}

        <ListItem
          button
          disableRipple
          onClick={() => setIntegration('POLAR')}
          selected={integration === 'POLAR'}
          className={classes.listItem}
        >
          <ListItemIcon>
            <img className="polar" src={PolarIcon} alt={`Polar`} />
          </ListItemIcon>
          <ListItemText primary={'' /*`Polar Flow`*/} />
          <ListItemIcon>{integration === 'POLAR' ? <CheckedCircleIcon /> : <EmptyCircleIcon />}</ListItemIcon>
        </ListItem>

        <ListItem
          button
          disableRipple
          onClick={() => setIntegration('SUUNTO')}
          selected={integration === 'SUUNTO'}
          className={classes.listItem}
        >
          <ListItemIcon>
            <img className="suunto" src={SuuntoIcon} alt={`Suunto`} />
          </ListItemIcon>
          <ListItemText primary={'' /*`Suunto`*/} />
          <ListItemIcon>{integration === 'SUUNTO' ? <CheckedCircleIcon /> : <EmptyCircleIcon />}</ListItemIcon>
        </ListItem>
      </List>

      {/*<Title>{t`Other`}</Title>
      <List>
        <ListItem button disableRipple onClick={handleOther} className={classes.listItem}>
          <ListItemText primary={t`Other`} style={{ marginLeft: '0' }} />
          <ListItemIcon>
            <ArrowForwardIcon />
          </ListItemIcon>
        </ListItem>
      </List>*/}

      {/*<Title>{t`No integration`}</Title>*/}
      <List>
        <ListItem
          button
          disableRipple
          onClick={() => setIntegration(null)}
          selected={integration === null}
          className={classes.listItem}
        >
          <ListItemIcon>{t`No integration`}</ListItemIcon>
          <ListItemText primary={'' /*t`No integration`*/} />
          <ListItemIcon>{integration === null ? <CheckedCircleIcon /> : <EmptyCircleIcon />}</ListItemIcon>
        </ListItem>
      </List>
    </>
  )

  const nextButton =
    integration === 'GOOGLE_FIT' ? (
      <div style={{ textAlign: 'center' }}>
        <YumButton className={classes.googleFitButton} to={doIntegration()}>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <div
              style={{
                backgroundColor: 'white',
                padding: '14px',
                height: '46px',
                lineHeight: '18px',
              }}
            >
              <GoogleIcon />
            </div>
            <span
              style={{
                margin: '0 14px',
                padding: '14px 0',
              }}
            >{t`Sign in with Google`}</span>
          </div>
        </YumButton>
      </div>
    ) : (
      <YumButton
        showLoader={true}
        disabled={submitInProgress}
        onClick={onClickIntegration}
        to={doIntegration()}
      >{t`Next`}</YumButton>
    )

  const actionButtons = <div className={classes.actionButtons}>{nextButton}</div>

  const form = (
    <>
      {mainForm}
      {actionButtons}
    </>
  )

  const content = (
    <>
      <Typography variant="subtitle2" style={{ marginBottom: '24px' }}>{t`Choose integration`}</Typography>
      {form}
    </>
  )

  return isDuringRegistration ? (
    content
  ) : (
    <Card className="onboarding">
      <div className="onboarding__form">
        <NotificationsWrapper />
        {content}
      </div>
      <DesktopSidebar />
    </Card>
  )
}
